import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  Clear as ClearIcon,
  Cloud as CloudIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  Search as SearchIcon,
  ViewModule as AcrIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { usePermissions } from '../../context/AuthContext';
import SettingsErrorAlert from './SettingsErrorAlert';

const DEFAULT_OFFERED_TAG_COUNT = 6;

const PROVIDER_TABS = [
  { id: 'azure', label: 'Azure', accent: '#0078d4', panelBg: 'linear-gradient(165deg, #e8f4fc 0%, #cfe8f9 45%, #b3d9f2 100%)', itemBg: 'linear-gradient(135deg, #0ea5e9 0%, #0369a1 100%)' },
  { id: 'gcp', label: 'GCP', accent: '#34a853', panelBg: 'linear-gradient(165deg, #e8f8ee 0%, #c8edd6 45%, #a8e0c0 100%)', itemBg: 'linear-gradient(135deg, #34a853 0%, #137333 100%)' },
  { id: 'aws', label: 'AWS', accent: '#ff9900', panelBg: 'linear-gradient(165deg, #fff6e8 0%, #ffe4c2 45%, #ffd199 100%)', itemBg: 'linear-gradient(135deg, #ff9900 0%, #cc7a00 100%)' }
];

const emptyForm = {
  id: '',
  provider: 'azure',
  label: '',
  loginServer: '',
  acrName: '',
  username: '',
  password: '',
  repositoryFilter: ''
};

const countOfferedTags = (enabledImages = {}) =>
  Object.values(enabledImages).reduce((sum, tags) => {
    if (tags === null) return sum;
    return sum + (Array.isArray(tags) ? tags.length : 0);
  }, 0);

const AcrImagesSettings = () => {
  const { can } = usePermissions();
  const canEdit = can('settings', 'edit');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [providerTab, setProviderTab] = useState(0);
  const [registries, setRegistries] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [images, setImages] = useState([]);
  const [enabledImages, setEnabledImages] = useState({});
  const [focusedRepo, setFocusedRepo] = useState(null);
  const [catalogError, setCatalogError] = useState('');
  const [registryHost, setRegistryHost] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [tagSearchInput, setTagSearchInput] = useState('');
  const [tagSearchQuery, setTagSearchQuery] = useState('');
  const [credentialsSecret, setCredentialsSecret] = useState('tenanthub-acr-registry-secrets');
  const [k8sSecretsAvailable, setK8sSecretsAvailable] = useState(false);
  const [defaultTagCount, setDefaultTagCount] = useState(DEFAULT_OFFERED_TAG_COUNT);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('add');
  const [form, setForm] = useState(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [repositoryLinks, setRepositoryLinks] = useState({});
  const [catalogOptions, setCatalogOptions] = useState([]);
  const [cacheNote, setCacheNote] = useState('');

  const activeProvider = PROVIDER_TABS[providerTab]?.id || 'azure';
  const providerMeta = PROVIDER_TABS[providerTab] || PROVIDER_TABS[0];

  const applyPayload = (data, preferId) => {
    const list = data.registries || [];
    setRegistries(list);
    setCredentialsSecret(data.credentials_secret || 'tenanthub-acr-registry-secrets');
    setK8sSecretsAvailable(Boolean(data.k8s_secrets_available));
    setDefaultTagCount(data.default_offered_tag_count || DEFAULT_OFFERED_TAG_COUNT);
    const id = preferId
      || data.selected_registry_id
      || data.active_registry_id
      || data.active?.id
      || list[0]?.id
      || '';
    setSelectedId(id);
    const selected = list.find((r) => r.id === id);
    setRegistryHost(data.registry || data.active?.loginServer || '');
    setCatalogError(data.catalog_error || '');
    setImages(data.images || []);
    setRepositoryLinks(data.repository_links || selected?.repository_links || {});
    if (data.cache?.from_cache) {
      setCacheNote(
        data.cache.unchanged
          ? `Using cached catalog${data.cache.pulled_at ? ` (last pull ${new Date(data.cache.pulled_at).toLocaleString()})` : ''}. “Check for new images” only updates when tags change.`
          : 'Loaded from registry cache.'
      );
    } else if (data.cache?.pulled_at) {
      setCacheNote(`Registry catalog refreshed at ${new Date(data.cache.pulled_at).toLocaleString()}.`);
    } else {
      setCacheNote('');
    }
    const nextEnabled = data.enabled_images || selected?.enabled_images || {};
    setEnabledImages(nextEnabled);
    const firstOffered = Object.keys(nextEnabled)[0] || null;
    setFocusedRepo(firstOffered);
    setTagSearchInput('');
    setTagSearchQuery('');
  };

  const load = useCallback(async (registryId, { refresh = false } = {}) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (registryId) params.set('registry_id', registryId);
      if (refresh) params.set('refresh', 'true');
      const qs = params.toString() ? `?${params.toString()}` : '';
      const response = await api.get(`/settings/acr-images${qs}`);
      applyPayload(response.data.data || {}, registryId);
    } catch (err) {
      setError({
        message: err.response?.data?.message || err.message || 'Failed to load registry settings',
        path: err.response?.data?.path,
        status: err.response?.status
      });
      setImages([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    Promise.all([
      api.get('/catalog/applications'),
      api.get('/catalog/services')
    ]).then(([appsRes, svcRes]) => {
      const apps = (appsRes.data?.data || [])
        .filter((a) => a.short_name)
        .map((a) => ({
          value: `application:${a.short_name}`,
          kind: 'application',
          short_name: a.short_name,
          catalog_id: a.id,
          label: `App · ${a.name} (${a.short_name})`
        }));
      const svcs = (svcRes.data?.data || [])
        .filter((s) => s.short_name)
        .map((s) => ({
          value: `service:${s.short_name}`,
          kind: 'service',
          short_name: s.short_name,
          catalog_id: s.id,
          label: `Service · ${s.name} (${s.short_name})`
        }));
      setCatalogOptions([...apps, ...svcs]);
    }).catch(() => setCatalogOptions([]));
  }, [load]);

  const providerRegistries = useMemo(
    () => registries.filter((r) => (r.provider || 'azure') === activeProvider),
    [registries, activeProvider]
  );

  const selected = useMemo(
    () => registries.find((r) => r.id === selectedId) || null,
    [registries, selectedId]
  );

  const filteredRepos = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return images;
    return images.filter((row) => {
      const hay = `${row.repository} ${row.image || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [images, searchQuery]);

  const activeRepoRow = useMemo(
    () => images.find((r) => r.repository === focusedRepo) || null,
    [images, focusedRepo]
  );

  const filteredTags = useMemo(() => {
    const tags = activeRepoRow?.tags || [];
    const q = tagSearchQuery.trim().toLowerCase();
    if (!q) return tags;
    return tags.filter((t) => String(t).toLowerCase().includes(q));
  }, [activeRepoRow, tagSearchQuery]);

  const offeredRepoCount = Object.keys(enabledImages).filter((repo) => {
    const tags = enabledImages[repo];
    return tags === null || (Array.isArray(tags) && tags.length > 0);
  }).length;
  const offeredTagCount = countOfferedTags(enabledImages);
  const focusedTagSet = useMemo(() => {
    const tags = enabledImages[focusedRepo];
    if (tags === null) return new Set(activeRepoRow?.tags || []);
    return new Set(Array.isArray(tags) ? tags : []);
  }, [enabledImages, focusedRepo, activeRepoRow]);
  const checkedTagCount = focusedTagSet.size;

  const defaultTagsForRepo = (repository) => {
    const row = images.find((r) => r.repository === repository);
    const tags = row?.tags || [];
    const existing = enabledImages[repository];
    if (Array.isArray(existing) && existing.length > 0) return existing;
    if (existing === null) return tags;
    return tags.slice(0, defaultTagCount);
  };

  const openAdd = () => {
    setDialogMode('add');
    setForm({ ...emptyForm, provider: activeProvider });
    setDialogOpen(true);
  };

  const openEdit = (reg, e) => {
    e?.stopPropagation?.();
    setDialogMode('edit');
    setForm({
      id: reg.id,
      provider: reg.provider || 'azure',
      label: reg.label || '',
      loginServer: reg.loginServer || '',
      acrName: reg.acrName || '',
      username: reg.username || '',
      password: '',
      repositoryFilter: reg.repositoryFilter || '',
      password_set: Boolean(reg.password_set)
    });
    setDialogOpen(true);
  };

  const onFormChange = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'loginServer' && !prev.acrName) {
        next.acrName = String(value).replace(/\.azurecr\.io$/i, '').replace(/^https?:\/\//, '');
      }
      return next;
    });
  };

  const selectRegistry = async (reg) => {
    if (!reg || reg.id === selectedId) return;
    setSelectedId(reg.id);
    setFocusedRepo(null);
    setLoading(true);
    try {
      if (canEdit) {
        const response = await api.post(`/settings/acr-images/registries/${reg.id}/select`);
        applyPayload(response.data.data || {}, reg.id);
      } else {
        await load(reg.id);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load registry images');
      await load(reg.id);
    } finally {
      setLoading(false);
    }
  };

  const focusRepository = (repository) => {
    if (!Object.prototype.hasOwnProperty.call(enabledImages, repository)) return;
    setFocusedRepo(repository);
    setTagSearchInput('');
    setTagSearchQuery('');
  };

  const toggleRepository = (repository, checked) => {
    if (!canEdit) return;
    if (checked) {
      const tags = defaultTagsForRepo(repository);
      setEnabledImages((prev) => ({ ...prev, [repository]: tags }));
      setFocusedRepo(repository);
      setTagSearchInput('');
      setTagSearchQuery('');
      return;
    }
    setEnabledImages((prev) => {
      const next = { ...prev };
      delete next[repository];
      return next;
    });
    setFocusedRepo((focused) => (focused === repository ? null : focused));
  };

  const toggleTag = (tag, checked) => {
    if (!canEdit || !focusedRepo) return;
    const allTags = activeRepoRow?.tags || [];
    setEnabledImages((prev) => {
      const current = prev[focusedRepo] === null
        ? [...allTags]
        : (Array.isArray(prev[focusedRepo]) ? [...prev[focusedRepo]] : []);
      let nextTags;
      if (checked) {
        nextTags = allTags.filter((t) => current.includes(t) || t === tag);
      } else {
        nextTags = current.filter((t) => t !== tag);
      }
      return { ...prev, [focusedRepo]: nextTags };
    });
  };

  const selectVisibleTags = (checked) => {
    if (!canEdit || !focusedRepo) return;
    const allTags = activeRepoRow?.tags || [];
    setEnabledImages((prev) => {
      const current = prev[focusedRepo] === null
        ? [...allTags]
        : (Array.isArray(prev[focusedRepo]) ? [...prev[focusedRepo]] : []);
      let nextTags;
      if (checked) {
        const add = new Set([...current, ...filteredTags]);
        nextTags = allTags.filter((t) => add.has(t));
      } else {
        const remove = new Set(filteredTags);
        nextTags = current.filter((t) => !remove.has(t));
      }
      return { ...prev, [focusedRepo]: nextTags };
    });
  };

  const saveOfferings = async () => {
    if (!canEdit || !selectedId) return;
    setSaving(true);
    try {
      // Drop empty tag lists so unchecked-all repos are not offered
      const payload = {};
      Object.entries(enabledImages).forEach(([repo, tags]) => {
        if (tags === null) {
          payload[repo] = null;
          return;
        }
        if (Array.isArray(tags) && tags.length > 0) payload[repo] = tags;
      });

      const response = await api.put(`/settings/acr-images/registries/${selectedId}/offerings`, {
        enabled_images: payload
      });
      const updated = response.data.data?.registry;
      const next = updated?.enabled_images || payload;
      setEnabledImages(next);
      setRegistries((prev) => prev.map((r) => (
        r.id === selectedId
          ? {
            ...r,
            enabled_images: next,
            enabled_repositories: updated?.enabled_repositories || Object.keys(next)
          }
          : r
      )));
      toast.success(
        `Saved ${Object.keys(next).length} repository offering(s) · ${countOfferedTags(next)} image(s)`
      );
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save offerings');
    } finally {
      setSaving(false);
    }
  };

  const submitDialog = async () => {
    if (!canEdit) return;
    if (form.provider !== 'azure' && activeProvider !== 'azure') {
      toast.info(`${PROVIDER_TABS.find((p) => p.id === form.provider)?.label || form.provider} support is coming soon`);
      return;
    }
    if (!form.loginServer.trim()) {
      toast.error('Login server is required (e.g. myregistry.azurecr.io)');
      return;
    }
    if (dialogMode === 'add' && !form.password.trim()) {
      toast.error('Password / token is required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        provider: form.provider || 'azure',
        label: form.label.trim() || form.acrName.trim() || form.loginServer.trim(),
        loginServer: form.loginServer.trim(),
        acrName: form.acrName.trim(),
        username: form.username.trim(),
        repositoryFilter: form.repositoryFilter,
        ...(form.password.trim() ? { password: form.password.trim() } : {})
      };

      if (dialogMode === 'add') {
        const response = await api.post('/settings/acr-images/registries', payload);
        const data = response.data.data || {};
        setDialogOpen(false);
        toast.success('Registry added — credentials stored in Kubernetes Secret');
        const newId = data.registry?.id;
        if (newId) {
          const selectRes = await api.post(`/settings/acr-images/registries/${newId}/select`);
          applyPayload(selectRes.data.data || {}, newId);
        } else {
          await load();
        }
      } else {
        await api.put(`/settings/acr-images/registries/${form.id}`, payload);
        setDialogOpen(false);
        toast.success('Registry updated');
        await load(form.id);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save registry');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!canEdit || !deleteTarget) return;
    setSaving(true);
    try {
      const response = await api.delete(`/settings/acr-images/registries/${deleteTarget.id}`);
      applyPayload(response.data.data || {});
      setDeleteTarget(null);
      toast.success('Registry removed');
      const nextId = response.data.data?.active_registry_id;
      if (nextId) await load(nextId);
      else await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete registry');
    } finally {
      setSaving(false);
    }
  };

  if (loading && registries.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <AcrIcon sx={{ color: providerMeta.accent }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800 }}>
              Container Images
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Select a registry, check repositories, then pick image tags in the panel next to them.
              Latest {defaultTagCount} images are auto-selected. Click Save when done. Credentials live in
              Kubernetes Secret
              {' '}
              <code>{credentialsSecret}</code>
              {k8sSecretsAvailable ? '' : ' (env fallback when not in-cluster)'}.
            </Typography>
          </Box>
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={() => load(selectedId, { refresh: true })} disabled={saving}>
          Check for new images
        </Button>
      </Box>

      {error && (
        <SettingsErrorAlert
          message={error.message}
          path={error.path}
          status={error.status}
        />
      )}

      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={providerTab}
          onChange={(_, v) => setProviderTab(v)}
          sx={{
            px: 1,
            minHeight: 48,
            '& .MuiTab-root': { fontWeight: 700, textTransform: 'none', minHeight: 48 }
          }}
        >
          {PROVIDER_TABS.map((tab) => (
            <Tab
              key={tab.id}
              icon={<CloudIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label={tab.label}
            />
          ))}
        </Tabs>
      </Paper>

      {activeProvider !== 'azure' ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {providerMeta.label}
          {' '}
          container registry support is coming soon. Use the Azure tab to manage ACR connections today.
        </Alert>
      ) : null}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '340px 1fr' },
          gap: 2,
          alignItems: 'stretch',
          minHeight: 520
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 2,
            background: providerMeta.panelBg,
            border: `1px solid ${providerMeta.accent}33`,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              {providerMeta.label}
              {' '}
              registries
            </Typography>
            {canEdit && activeProvider === 'azure' && (
              <Button
                size="small"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={openAdd}
                sx={{ bgcolor: providerMeta.accent, '&:hover': { bgcolor: providerMeta.accent } }}
              >
                Add
              </Button>
            )}
          </Box>

          <Typography variant="caption" color="text.secondary">
            Click a registry to load repositories and images.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, flex: 1, overflow: 'auto' }}>
            {providerRegistries.length === 0 ? (
              <Alert severity="info" sx={{ bgcolor: 'rgba(255,255,255,0.7)' }}>
                No
                {' '}
                {providerMeta.label}
                {' '}
                registries yet.
                {canEdit && activeProvider === 'azure' ? ' Click Add to connect an ACR.' : ''}
              </Alert>
            ) : (
              providerRegistries.map((reg) => {
                const active = reg.id === selectedId;
                const tagN = countOfferedTags(reg.enabled_images || {});
                const repoN = Object.keys(reg.enabled_images || {}).length;
                return (
                  <Box
                    key={reg.id}
                    onClick={() => selectRegistry(reg)}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      cursor: 'pointer',
                      color: '#fff',
                      background: providerMeta.itemBg,
                      boxShadow: active ? '0 0 0 3px rgba(255,255,255,0.85)' : '0 4px 14px rgba(0,0,0,0.12)',
                      opacity: active ? 1 : 0.88,
                      transition: 'transform 0.15s ease, opacity 0.15s ease',
                      '&:hover': { opacity: 1, transform: 'translateY(-1px)' }
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography fontWeight={800} noWrap>
                          {reg.label || reg.acrName || 'Registry'}
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.9, display: 'block' }} noWrap>
                          {reg.loginServer}
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.85 }}>
                          {repoN}
                          {' '}
                          repo(s) ·
                          {' '}
                          {tagN}
                          {' '}
                          image(s) ·
                          {' '}
                          {reg.password_set ? 'secret set' : 'no secret'}
                        </Typography>
                      </Box>
                      {canEdit && (
                        <Box sx={{ display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
                          <Tooltip title="Edit">
                            <IconButton size="small" sx={{ color: '#fff' }} onClick={(e) => openEdit(reg, e)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              sx={{ color: '#fff' }}
                              disabled={registries.length <= 1}
                              onClick={() => setDeleteTarget(reg)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            background: 'linear-gradient(165deg, #f8fafc 0%, #eef2f7 50%, #e8eef6 100%)',
            border: '1px solid #cbd5e1',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 480
          }}
        >
          {!selected || activeProvider !== 'azure' ? (
            <Box sx={{ m: 'auto', textAlign: 'center', maxWidth: 360 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Select a registry
              </Typography>
              <Typography color="text.secondary">
                Choose an ACR on the left. Then check repositories and pick image tags in the two panels below.
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
                <Box>
                  <Typography variant="h6" fontWeight={800}>
                    {selected.label || selected.acrName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {registryHost || selected.loginServer}
                    {' · '}
                    <strong>{offeredRepoCount}</strong>
                    {' '}
                    repositories /
                    {' '}
                    <strong>{offeredTagCount}</strong>
                    {' '}
                    images selected
                  </Typography>
                </Box>
                {canEdit && (
                  <Button
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    onClick={saveOfferings}
                    disabled={saving || loading}
                    sx={{ background: providerMeta.itemBg }}
                  >
                    Save
                  </Button>
                )}
              </Box>

              {catalogError && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {catalogError}
                </Alert>
              )}

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress />
                </Box>
              ) : images.length === 0 ? (
                <Alert severity="info">
                  No repositories loaded. Edit the registry credentials if listing failed.
                </Alert>
              ) : (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 2,
                    flex: 1,
                    minHeight: 0
                  }}
                >
                  {/* Repositories vertical box */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      display: 'flex',
                      flexDirection: 'column',
                      border: `1px solid ${providerMeta.accent}44`,
                      background: 'linear-gradient(180deg, #e0f2fe 0%, #f0f9ff 100%)',
                      minHeight: 360,
                      maxHeight: 560
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.5, color: '#0369a1' }}>
                      Repositories
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                      Check a repository to offer it. Images appear in the box on the right.
                    </Typography>
                    <TextField
                      size="small"
                      placeholder="Search repositories…"
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          setSearchQuery(searchInput.trim());
                        }
                      }}
                      sx={{ mb: 1, bgcolor: '#fff' }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                        endAdornment: searchInput ? (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSearchInput('');
                                setSearchQuery('');
                              }}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ) : null
                      }}
                    />
                    <Box sx={{ overflow: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      {filteredRepos.length === 0 ? (
                        <Alert severity="info">No repositories match the search.</Alert>
                      ) : (
                        filteredRepos.map((row) => {
                          const checked = Object.prototype.hasOwnProperty.call(enabledImages, row.repository);
                          const focused = focusedRepo === row.repository;
                          const tagN = Array.isArray(enabledImages[row.repository])
                            ? enabledImages[row.repository].length
                            : (enabledImages[row.repository] === null ? (row.tags || []).length : 0);
                          return (
                            <Box
                              key={row.repository}
                              onClick={() => {
                                if (checked) focusRepository(row.repository);
                              }}
                              sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 0.5,
                                p: 1,
                                borderRadius: 1.5,
                                bgcolor: focused ? 'rgba(3,105,161,0.12)' : 'rgba(255,255,255,0.85)',
                                border: focused ? '2px solid #0284c7' : '1px solid #bae6fd',
                                cursor: checked ? 'pointer' : 'default'
                              }}
                            >
                              <Checkbox
                                size="small"
                                checked={checked}
                                disabled={!canEdit}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => toggleRepository(row.repository, e.target.checked)}
                                sx={{ pt: 0.25 }}
                              />
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography fontWeight={700} sx={{ wordBreak: 'break-word', lineHeight: 1.3 }}>
                                  {row.repository}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {(row.tags || []).length}
                                  {' '}
                                  image(s)
                                  {checked ? ` · ${tagN} selected` : ''}
                                  {repositoryLinks[row.repository]?.short_name
                                    ? ` · ${repositoryLinks[row.repository].kind}:${repositoryLinks[row.repository].short_name}`
                                    : ''}
                                  {row.error ? ` · ${row.error}` : ''}
                                </Typography>
                              </Box>
                            </Box>
                          );
                        })
                      )}
                    </Box>
                  </Paper>

                  {/* Images vertical box */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      display: 'flex',
                      flexDirection: 'column',
                      border: '1px solid #99f6e4',
                      background: 'linear-gradient(180deg, #ccfbf1 0%, #f0fdfa 100%)',
                      minHeight: 360,
                      maxHeight: 560
                    }}
                  >
                    {!focusedRepo || !Object.prototype.hasOwnProperty.call(enabledImages, focusedRepo) ? (
                      <Box sx={{ m: 'auto', textAlign: 'center', px: 2 }}>
                        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                          Images
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Check a repository on the left to list its images here.
                          The latest
                          {' '}
                          {defaultTagCount}
                          {' '}
                          are selected automatically.
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        <Typography variant="subtitle2" fontWeight={800} sx={{ color: '#0f766e', wordBreak: 'break-word' }}>
                          Images ·
                          {' '}
                          {focusedRepo}
                        </Typography>
                        {canEdit && (
                          <FormControl size="small" fullWidth sx={{ mt: 1, mb: 1, bgcolor: 'rgba(255,255,255,0.9)', borderRadius: 1 }}>
                            <InputLabel>Catalog category</InputLabel>
                            <Select
                              label="Catalog category"
                              value={
                                repositoryLinks[focusedRepo]
                                  ? `${repositoryLinks[focusedRepo].kind}:${repositoryLinks[focusedRepo].short_name}`
                                  : ''
                              }
                              onChange={async (e) => {
                                const val = e.target.value;
                                const opt = catalogOptions.find((o) => o.value === val);
                                try {
                                  const body = val && opt
                                    ? {
                                      repository: focusedRepo,
                                      kind: opt.kind,
                                      short_name: opt.short_name,
                                      catalog_id: opt.catalog_id,
                                      label: opt.label
                                    }
                                    : { repository: focusedRepo, clear: true };
                                  const response = await api.put(
                                    `/settings/acr-images/registries/${selectedId}/repository-link`,
                                    body
                                  );
                                  const links = response.data?.data?.registry?.repository_links || {};
                                  setRepositoryLinks(links);
                                  toast.success(val ? 'Repository categorized' : 'Category cleared');
                                } catch (err) {
                                  toast.error(err.response?.data?.message || 'Failed to save category');
                                }
                              }}
                            >
                              <MenuItem value="">
                                <em>Uncategorized</em>
                              </MenuItem>
                              {catalogOptions.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                        {cacheNote ? (
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                            {cacheNote}
                          </Typography>
                        ) : null}
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                          Newest first. Latest
                          {' '}
                          {defaultTagCount}
                          {' '}
                          auto-selected —
                          {' '}
                          <strong>{checkedTagCount}</strong>
                          {' '}
                          of
                          {' '}
                          {(activeRepoRow?.tags || []).length}
                          {' '}
                          checked. Use checkboxes to change, then Save.
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                          <TextField
                            size="small"
                            placeholder="Search images…"
                            value={tagSearchInput}
                            onChange={(e) => setTagSearchInput(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                setTagSearchQuery(tagSearchInput.trim());
                              }
                            }}
                            sx={{ flex: '1 1 140px', bgcolor: '#fff' }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon fontSize="small" />
                                </InputAdornment>
                              )
                            }}
                          />
                          {canEdit && (
                            <>
                              <Button size="small" onClick={() => selectVisibleTags(true)}>Check shown</Button>
                              <Button size="small" onClick={() => selectVisibleTags(false)}>Uncheck shown</Button>
                            </>
                          )}
                        </Box>
                        <Box sx={{ overflow: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                          {(activeRepoRow?.tags || []).length === 0 ? (
                            <Alert severity="info">
                              {activeRepoRow?.error || 'No images found in this repository.'}
                            </Alert>
                          ) : filteredTags.length === 0 ? (
                            <Alert severity="info">
                              No images match “
                              {tagSearchQuery}
                              ”.
                            </Alert>
                          ) : (
                            filteredTags.map((tag, idx) => {
                              const on = focusedTagSet.has(tag);
                              const allTags = activeRepoRow?.tags || [];
                              const absoluteIdx = allTags.indexOf(tag);
                              const isDefaultBand = absoluteIdx >= 0 && absoluteIdx < defaultTagCount;
                              return (
                                <Box
                                  key={tag}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 0.5,
                                    p: 1,
                                    borderRadius: 1.5,
                                    bgcolor: on ? 'rgba(13,148,136,0.12)' : 'rgba(255,255,255,0.9)',
                                    border: on ? '1px solid #14b8a6' : '1px solid #99f6e4'
                                  }}
                                >
                                  <Checkbox
                                    size="small"
                                    checked={on}
                                    disabled={!canEdit}
                                    onChange={(e) => toggleTag(tag, e.target.checked)}
                                    sx={{ pt: 0.25 }}
                                  />
                                  <Box sx={{ minWidth: 0 }}>
                                    <Typography
                                      fontWeight={700}
                                      sx={{
                                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                        wordBreak: 'break-all',
                                        fontSize: '0.85rem'
                                      }}
                                    >
                                      {tag}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {on ? 'Selected for Add Tenant' : 'Not selected'}
                                      {isDefaultBand ? ' · latest 6' : ''}
                                    </Typography>
                                  </Box>
                                </Box>
                              );
                            })
                          )}
                        </Box>
                      </>
                    )}
                  </Paper>
                </Box>
              )}
            </>
          )}
        </Paper>
      </Box>

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogMode === 'add' ? 'Add container registry' : `Edit: ${form.label || form.loginServer}`}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Alert severity="info">
            Password / token is written to Kubernetes Secret
            {' '}
            <strong>{credentialsSecret}</strong>
            ; it is never stored in the settings database.
          </Alert>
          <TextField
            label="Label"
            value={form.label}
            onChange={onFormChange('label')}
            fullWidth
            size="small"
            placeholder="Pilvi prod ACR"
          />
          <TextField
            label="Login server"
            value={form.loginServer}
            onChange={onFormChange('loginServer')}
            fullWidth
            size="small"
            required
            placeholder="myregistry.azurecr.io"
            helperText="Hostname only — no https://"
          />
          <TextField
            label="ACR name"
            value={form.acrName}
            onChange={onFormChange('acrName')}
            fullWidth
            size="small"
            placeholder="myregistry"
          />
          <TextField
            label="Username"
            value={form.username}
            onChange={onFormChange('username')}
            fullWidth
            size="small"
            placeholder="token name or admin user"
            autoComplete="off"
          />
          <TextField
            label="Password / token"
            type="password"
            value={form.password}
            onChange={onFormChange('password')}
            fullWidth
            size="small"
            required={dialogMode === 'add'}
            placeholder={form.password_set ? '•••••••• (leave blank to keep)' : 'Password'}
            helperText={dialogMode === 'edit' && form.password_set ? 'Leave blank to keep the existing secret' : 'Stored in Kubernetes Secret'}
            autoComplete="new-password"
          />
          <TextField
            label="Repository filter (optional)"
            value={form.repositoryFilter}
            onChange={onFormChange('repositoryFilter')}
            fullWidth
            size="small"
            placeholder="erp4c,tenanthub"
            helperText="Comma-separated substrings; empty = list all"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submitDialog}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          >
            {dialogMode === 'add' ? 'Add registry' : 'Save changes'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete registry</DialogTitle>
        <DialogContent>
          Remove
          {' '}
          <strong>{deleteTarget?.label || deleteTarget?.loginServer}</strong>
          ? The credential key will also be deleted from the Kubernetes Secret.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDelete} disabled={saving}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AcrImagesSettings;
